import { AxiosError } from 'axios';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { GenericErrorResponse } from '../api/model';
import { useAuth } from '../modules/profile/logic/auth';
import RouteKey from '../routes/key';
import { CUSTOMIZED_UNAUTHORIZED_STATUS_CODE } from '../api/config/misc';
import { GET_USER_DETAILS_QUERY_KEY } from '../constants/queryKeys';

/**
 * API service error that wrap with AxiosError
 */
export declare type ServiceError = AxiosError<GenericErrorResponse>;

const { error } = RouteKey.child;
const { serverError, generic, serviceUnavailable } = RouteKey.error.child;

export interface ErrorReturn<T> {
  /**
   * Function to handle session related api error.
   */
  session: (err: null | AxiosError<T>, isRedirect?: boolean) => boolean;
  /**
   * Function to handle general api error
   */
  api: (err: null | AxiosError<T>, isRedirect?: boolean) => string;
}

/**
 * A hook that provides global error handlers
 * @returns
 */
export function useError<T>(): ErrorReturn<T> {
  const queryClient = useQueryClient();
  const history = useHistory();
  const authHook = useAuth();

  const session = (err: AxiosError<T> | null, isRedirect = true) => {
    // eslint-disable-next-line no-underscore-dangle
    let _authorized = true;
    if (err?.response) {
      // forbidden, not authorized call
      switch (err.response.status) {
        // private route already handle, unless hack to change
        case 403:
          _authorized = false;
          if (isRedirect) {
            history.push(`/${error}/${generic}`);
          }
          break;
        // session expire
        case CUSTOMIZED_UNAUTHORIZED_STATUS_CODE:
          _authorized = false;
          queryClient.invalidateQueries(GET_USER_DETAILS_QUERY_KEY);
          authHook.signout();
          break;
        default:
          break;
      }
    }
    return _authorized;
  };

  const api = (err: AxiosError<T> | null, isRedirect = true) => {
    // eslint-disable-next-line no-underscore-dangle
    let _error = '';
    if (err?.response) {
      // 404 or 5xx error handling
      switch (err.response.status) {
        // Api route not exist
        case 404:
          _error = 'Api Route Not Found';
          if (isRedirect && !history.location.pathname.includes(`/${error}`)) {
            history.replace(`/${error}`);
          }
          break;
        // Internal server error
        case 500:
        case 502:
        case 504:
          _error = 'Server Error';

          if (isRedirect && !history.location.pathname.includes(`/${error}`)) {
            history.replace(`/${error}/${serverError}`);
          }
          break;
        case 503:
          _error = 'Service Unavailable';
          if (isRedirect && !history.location.pathname.includes(`/${error}`)) {
            history.replace(`/${error}/${serviceUnavailable}`);
          }
          break;
        default:
          break;
      }
    }
    return _error;
  };
  return {
    session,
    api,
  };
}
